// src/utils/doubleElimWaves.js
// Pure wave + match-count helper for double-elimination brackets.
// Mirrors the exact wave schedule and round-name rules of
// functions/modules/bracketEngine.js buildDoubleElimination:
//   - Winners rounds 1..W (W = log2(nextPow2(n)), matches = size / 2^r)
//   - Losers rounds 1..L (L = 2 * (W - 1), matches = size / 2^(ceil(k/2) + 1))
//   - Grand Final (1 match) and optional Grand Final Reset (1 match, default on)

function nextPow2(n) {
  let p = 2;
  while (p < n) p *= 2;
  return p;
}

function roundNameFor(bracket, r, total) {
  if (bracket === 'GF') return r === 2 ? 'Grand Final Reset' : 'Grand Final';
  const prefix = bracket === 'W' ? 'Winners' : 'Losers';
  if (r === total) return `${prefix} Final`;
  if (r === total - 1 && total >= (bracket === 'W' ? 3 : 4)) return `${prefix} Semifinal`;
  return `${prefix} Round ${r}`;
}

export function doubleElimWaves(bracketSize, opts = {}) {
  const num = Math.floor(Number(bracketSize) || 0);
  if (num < 4) return [];

  const size = nextPow2(num);
  const W = Math.log2(size);
  const L = 2 * (W - 1);
  const reset = opts?.grandFinalReset !== false;

  const wave = {};
  for (let r = 1; r <= W; r += 1) {
    wave[`W${r}`] = r;
  }
  for (let k = 1; k <= L; k += 1) {
    const prev = k === 1 ? wave.W1 + 1 : wave[`L${k - 1}`] + 1;
    const drop = k % 2 === 0 ? wave[`W${k / 2 + 1}`] + 1 : 0;
    wave[`L${k}`] = Math.max(prev, drop);
  }
  const gfWave = Math.max(wave[`W${W}`], wave[`L${L}`]) + 1;

  const counts = {};
  const roundNamesByWave = {};
  const addRound = (w, matchCount, rName) => {
    counts[w] = (counts[w] || 0) + matchCount;
    if (!roundNamesByWave[w]) roundNamesByWave[w] = [];
    roundNamesByWave[w].push(rName);
  };

  for (let r = 1; r <= W; r += 1) {
    addRound(wave[`W${r}`], size / (2 ** r), roundNameFor('W', r, W));
  }
  for (let k = 1; k <= L; k += 1) {
    addRound(wave[`L${k}`], size / (2 ** (Math.ceil(k / 2) + 1)), roundNameFor('L', k, L));
  }
  addRound(gfWave, 1, roundNameFor('GF', 1, reset ? 2 : 1));
  if (reset) {
    addRound(gfWave + 1, 1, roundNameFor('GF', 2, 2));
  }

  const totalWaves = reset ? gfWave + 1 : gfWave;
  const out = [];
  for (let w = 1; w <= totalWaves; w += 1) {
    const roundNames = roundNamesByWave[w] || [];
    const label = roundNames.length === 1 ? roundNames[0] : roundNames.join(' + ');
    out.push({
      round: w,
      wave: w,
      matches: counts[w] || 0,
      label,
      roundNames,
    });
  }
  return out;
}

export default doubleElimWaves;

